import {ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, signal, TemplateRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormArray, FormGroup, Validators} from '@angular/forms';
import {ClearanceStatus} from 'typescript-models/common';
import {createAttentionPointGroup} from '../../operational-visit-editor.component';
import clearanceAttentionPoints from '@portbase/bezoekschip-service-typescriptmodels/refdata/clearance-attention-points.json';

export interface AttentionPointOption {
  type: string;
  description: string;
}

/**
 * Renders a single port-authority clearance field: the status select plus the
 * conditional remarks textarea and attention-point rows. The parent passes the
 * clearance {@link FormGroup} directly, so this component never reaches through
 * the surrounding FormGroupDirective.
 */
@Component({
  selector: 'app-operational-clearance-field',
  templateUrl: './operational-clearance-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OperationalClearanceFieldComponent {
  /** The clearance sub-group: { status, remarks, attentionPoints }. */
  readonly clearanceGroup = input.required<FormGroup>();
  /** Human-readable field label, e.g. "Visit". */
  readonly label = input.required<string>();
  /** Clearance code used for the validation message and attention-point lookup, e.g. "ekh". */
  readonly clearanceKey = input.required<string>();
  /** Shared status options (passed in so all fields stay in sync). */
  readonly statusOptions = input.required<ClearanceStatus[]>();
  /** Shared formatter template for the status select. */
  readonly formatterTemplate = input.required<TemplateRef<unknown>>();

  private readonly destroyRef = inject(DestroyRef);

  protected readonly errorLabel = computed(() => this.clearanceKey().toUpperCase());

  // Mirrors the status value in a signal so OnPush re-renders when it changes.
  private readonly status = signal<ClearanceStatus | null>(null);
  protected readonly showRemarks = computed(() =>
    this.status() === ClearanceStatus.REJECTED || this.status() === ClearanceStatus.APPROVED_WITH_REMARKS);

  constructor() {
    // Seed and track the status value for the currently bound clearance group.
    effect((onCleanup) => {
      const control = this.statusControl;
      this.status.set(control?.value ?? null);
      const sub = control?.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(value => this.status.set(value));
      onCleanup(() => sub?.unsubscribe());
    });
  }

  protected get statusControl() {
    return this.clearanceGroup().get('status');
  }

  protected get remarksControl() {
    return this.clearanceGroup().get('remarks');
  }

  protected get attentionPointsArray(): FormArray {
    return this.clearanceGroup().get('attentionPoints') as FormArray;
  }

  protected attentionPointOptions(): AttentionPointOption[] {
    return (clearanceAttentionPoints as Record<string, AttentionPointOption[]>)[this.clearanceKey()] ?? [];
  }

  /** Types already picked in other rows (excluding the row at {@param excludeIndex}). */
  private selectedTypesExcluding(excludeIndex: number): Set<string> {
    const types = new Set<string>();
    this.attentionPointsArray.controls.forEach((row, index) => {
      if (index === excludeIndex) {
        return;
      }
      const type = (row as FormGroup).get('type')?.value;
      if (type) {
        types.add(type);
      }
    });
    return types;
  }

  /**
   * Options available to the row at {@param index}: every type that isn't already
   * selected in another row. The row's own current selection stays included so it
   * remains visible/selectable in its own dropdown.
   */
  protected availableOptionsForRow(index: number): AttentionPointOption[] {
    const taken = this.selectedTypesExcluding(index);
    return this.attentionPointOptions().filter(option => !taken.has(option.type));
  }

  /**
   * True while another attention point can be added: there must be an unused type
   * AND the number of rows must stay below the number of available types (so you
   * can never create more rows — even empty ones — than there are types to pick).
   */
  protected get canAddAttentionPoint(): boolean {
    const options = this.attentionPointOptions();
    if (this.attentionPointsArray.length >= options.length) {
      return false;
    }
    const taken = this.selectedTypesExcluding(-1);
    return options.some(option => !taken.has(option.type));
  }

  protected addAttentionPoint(): void {
    if (!this.canAddAttentionPoint) {
      return;
    }
    const array = this.attentionPointsArray;
    array.push(createAttentionPointGroup(null, null));
    array.markAsDirty();
  }

  protected removeAttentionPoint(index: number): void {
    const array = this.attentionPointsArray;
    array.removeAt(index);
    array.markAsDirty();
  }

  protected selectedOptionForRow(index: number): AttentionPointOption | null {
    const type = (this.attentionPointsArray.at(index) as FormGroup)?.get('type')?.value;
    return type ? (this.attentionPointOptions().find(o => o.type === type) ?? null) : null;
  }

  protected attentionPointSelectedForRow(index: number, option: AttentionPointOption | null): void {
    const rowGroup = this.attentionPointsArray.at(index) as FormGroup;
    const typeControl = rowGroup?.get('type');
    const remarksControl = rowGroup?.get('remarks');
    typeControl?.setValue(option?.type ?? null);
    typeControl?.markAsDirty();
    // The remark is REQUIRED only once a type has been chosen for that row.
    if (option?.type) {
      remarksControl?.addValidators(Validators.required);
    } else {
      remarksControl?.removeValidators(Validators.required);
    }
    remarksControl?.updateValueAndValidity();
  }
}
